import { 
  createDocument, 
  getDocument, 
  updateDocument,
  queryDocuments
} from './firebase';
import { serverTimestamp } from 'firebase/firestore';
import { getCurrentUser } from './firebase';

const USER_PROFILES_COLLECTION = 'userProfiles';

/**
 * Create or update user profile in Firestore
 */
export const createUserProfile = async (userId, profileData) => {
  try {
    const userProfile = {
      userId,
      ...profileData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await createDocument(USER_PROFILES_COLLECTION, userProfile, userId);
    return userProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Get user profile by user ID
 */
export const getUserProfile = async (userId) => {
  try {
    const profile = await getDocument(USER_PROFILES_COLLECTION, userId);
    if (profile) {
      return {
        ...profile,
        createdAt: profile.createdAt?.toDate(),
        updatedAt: profile.updatedAt?.toDate()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    await updateDocument(USER_PROFILES_COLLECTION, userId, updateData);
    return updateData;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Get current user's profile
 */
export const getCurrentUserProfile = async () => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      return null;
    }
    
    return await getUserProfile(currentUser.uid);
  } catch (error) {
    console.error('Error getting current user profile:', error);
    return null;
  }
};

/**
 * Create user profile on first signup
 */
export const createInitialUserProfile = async (user) => {
  try {
    const profileData = {
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      preferences: {
        theme: 'system',
        notifications: {
          email: true,
          sms: false,
          marketing: true
        },
        units: 'metric'
      },
      solarInterests: {
        systemSize: '',
        budget: '',
        installationTimeline: '',
        propertyType: '',
        currentElectricityBill: '',
        loadSheddingConcerns: true
      },
      contactInformation: {
        phone: '',
        address: {
          street: '',
          city: '',
          province: '',
          postalCode: '',
          country: 'South Africa'
        }
      },
      metadata: {
        signupSource: 'website',
        firstLogin: serverTimestamp(),
        lastActive: serverTimestamp(),
        totalLogins: 1
      }
    };

    return await createUserProfile(user.uid, profileData);
  } catch (error) {
    console.error('Error creating initial user profile:', error);
    throw error;
  }
};

/**
 * Update user preferences
 */
export const updateUserPreferences = async (userId, preferences) => {
  try {
    const updates = {
      preferences: {
        ...preferences
      }
    };
    
    return await updateUserProfile(userId, updates);
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

/**
 * Update solar interests
 */
export const updateSolarInterests = async (userId, solarInterests) => {
  try {
    const updates = {
      solarInterests: {
        ...solarInterests
      }
    };
    
    return await updateUserProfile(userId, updates);
  } catch (error) {
    console.error('Error updating solar interests:', error);
    throw error;
  }
};

/**
 * Update contact information
 */
export const updateContactInformation = async (userId, contactInfo) => {
  try {
    const updates = {
      contactInformation: {
        ...contactInfo
      }
    };
    
    return await updateUserProfile(userId, updates);
  } catch (error) {
    console.error('Error updating contact information:', error);
    throw error;
  }
};

/**
 * Track user activity
 */
export const trackUserActivity = async (userId, activity) => {
  try {
    const profile = await getUserProfile(userId);
    if (!profile) return;

    const updates = {
      'metadata.lastActive': serverTimestamp(),
      'metadata.totalLogins': (profile.metadata?.totalLogins || 0) + 1
    };

    if (activity) {
      updates['metadata.lastActivity'] = activity;
    }

    return await updateUserProfile(userId, updates);
  } catch (error) {
    console.error('Error tracking user activity:', error);
    // Don't throw error for activity tracking
  }
};

/**
 * Get user statistics
 */
export const getUserStatistics = async (userId) => {
  try {
    const profile = await getUserProfile(userId);
    if (!profile) return null;

    // Get user's leads
    const userLeads = await queryDocuments('leads', [
      { field: 'userId', operator: '==', value: userId }
    ]);

    return {
      profile: profile.metadata || {},
      leadsGenerated: userLeads.length,
      memberSince: profile.createdAt,
      lastActive: profile.metadata?.lastActive?.toDate(),
      totalLogins: profile.metadata?.totalLogins || 0
    };
  } catch (error) {
    console.error('Error getting user statistics:', error);
    return null;
  }
};
